import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Icon from '../Icon';
import {
  calculateMonthlyExpenses,
  calculateCategoryAverages,
  calculateAnomalyThreshold,
  identifyAnomalies,
  findMainCauseCategory,
  findLargestExpense,
} from '../../utils/anomalyDetection';

/**
 * 이상 지출 탐지 카드
 * 월별 지출 통계를 기반으로 평균 대비 이상치를 탐지하여 표시한다.
 *
 * @param {Object} props
 * @param {Array} props.transactions - 전체 거래 데이터 (통계 기준)
 * @param {Array} props.filteredTransactions - 기간 필터링된 거래 데이터
 */
const AnomalyDetectionCard = ({ transactions, filteredTransactions }) => {
  const analysisResult = useMemo(() => {
    const allMonthlyExpenses = calculateMonthlyExpenses(transactions);
    const categoryAverages = calculateCategoryAverages(transactions);
    const allExpenseValues = Object.values(allMonthlyExpenses).filter((val) => val > 0);

    if (allExpenseValues.length < 3) {
      return { status: 'insufficient' };
    }

    const { mean, threshold } = calculateAnomalyThreshold(allExpenseValues);
    const filteredMonthlyExpenses = calculateMonthlyExpenses(filteredTransactions);
    const anomalies = identifyAnomalies(filteredMonthlyExpenses, threshold);

    if (anomalies.length === 0) {
      return { status: 'normal' };
    }

    const anomalyDetails = anomalies.map(([month, amount]) => {
      const excess = Math.max(0, amount - mean);
      const increasePercent = mean > 0 ? Math.round((excess / mean) * 100) : 0;

      const monthTransactions = filteredTransactions.filter(
        (t) => t.type === 'expense' && t.date.slice(0, 7) === month
      );

      const currentMonthCategories = monthTransactions.reduce((acc, t) => {
        const category = t.category || '미분류';
        acc[category] = (acc[category] || 0) + Number(t.amount);
        return acc;
      }, {});

      const mainCause = findMainCauseCategory(currentMonthCategories, categoryAverages);
      const largestExpense = findLargestExpense(monthTransactions);

      return {
        month,
        amount,
        increasePercent,
        excess,
        mainCause,
        largestExpense,
      };
    });

    return { status: 'anomalies', anomalyDetails };
  }, [transactions, filteredTransactions]);

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return `${year}년 ${parseInt(month)}월`;
  };

  return (
    <div className="anomaly-card">
      <div className="anomaly-card__header">
        <Icon name="warning" size={20} />
        <h3 className="anomaly-card__title">이상 지출 탐지</h3>
      </div>
      <div className="anomaly-card__content">
        {analysisResult.status === 'insufficient' && (
          <div className="anomaly-card__empty">
            <Icon name="info" size={48} color="#9CA3AF" />
            <p className="anomaly-card__empty-text">
              이상치 분석을 위해 최소 3개월 데이터가 필요합니다
            </p>
          </div>
        )}

        {analysisResult.status === 'normal' && (
          <div className="anomaly-card__empty">
            <Icon name="check_circle" size={48} color="#548164" />
            <p className="anomaly-card__empty-text">
              비정상적인 지출이 발견되지 않았습니다
            </p>
          </div>
        )}

        {analysisResult.status === 'anomalies' &&
          analysisResult.anomalyDetails.map((item) => (
            <div className="anomaly-card__item" key={item.month} tabIndex={0}>
              <div className="anomaly-card__item-header">
                <Icon name="error_outline" size={20} color="#C4554D" />
                <span className="anomaly-card__month">{formatMonth(item.month)}</span>
                <span className="anomaly-card__increase">+{item.increasePercent}%</span>
              </div>
              <div className="anomaly-card__amount">
                {item.amount.toLocaleString()}원
              </div>
              <div className="anomaly-card__excess">
                평균 대비 +{item.excess.toLocaleString()}원
              </div>
              <div className="anomaly-card__insights">
                <p>
                  주요 원인: {item.mainCause ? item.mainCause.name : '없음'}
                  {item.mainCause && ` (+${Math.round(item.mainCause.diff).toLocaleString()}원)`}
                </p>
                <p>
                  가장 큰 지출:{' '}
                  {item.largestExpense
                    ? `${item.largestExpense.description} (${item.largestExpense.amount.toLocaleString()}원)`
                    : '정보 없음'}
                </p>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

AnomalyDetectionCard.propTypes = {
  transactions: PropTypes.array.isRequired,
  filteredTransactions: PropTypes.array.isRequired,
};

export default AnomalyDetectionCard;
